<div class="parking-right-import-dialog">
    <h2 mat-dialog-title class="text-xl font-semibold mb-4">Parking Right Import and Mapping</h2>

    <mat-dialog-content>
        <mat-tab-group>
            <mat-tab label="Import CSV">
                <div class="tab-content">
                    <form [formGroup]="form" class="space-y-4">
                        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div class="flex items-center gap-2">
                                <mat-icon class="text-blue-600">info</mat-icon>
                                <div>
                                    <p class="font-medium text-gray-800">Parking Space: {{ parkingSpace.name || 'N/A' }}
                                    </p>
                                    <p class="text-sm text-gray-600">All imported members and parking rights will be assigned to
                                        this parking space</p>
                                </div>
                            </div>
                        </div>

                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                CSV File <span class="text-red-500">*</span>
                            </label>

                            @if (!fileName()) {
                            <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                                (click)="fileInput.click()">
                                <mat-icon class="text-4xl text-gray-400 mb-2">upload_file</mat-icon>
                                <p class="text-sm text-gray-600">Click to upload CSV file</p>
                                <p class="text-xs text-gray-500 mt-1">CSV files only</p>
                            </div>
                            <input #fileInput type="file" accept=".csv" (change)="onFileSelected($event)"
                                class="hidden" />
                            } @else {
                            <div
                                class="border border-gray-300 rounded-lg p-4 flex items-center justify-between bg-gray-50">
                                <div class="flex items-center gap-2">
                                    <mat-icon class="text-green-600">check_circle</mat-icon>
                                    <span class="text-sm font-medium">{{ fileName() }}</span>
                                </div>
                                <button mat-icon-button (click)="clearFile()" type="button">
                                    <mat-icon>close</mat-icon>
                                </button>
                            </div>
                            <input #fileInput type="file" accept=".csv" (change)="onFileSelected($event)"
                                class="hidden" />
                            }
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <mat-form-field>
                                <mat-label>Source Format</mat-label>
                                <mat-select formControlName="source">
                                    @for (opt of sourceOptions; track opt.value) {
                                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                                    }
                                </mat-select>
                                <mat-hint>How the CSV columns should be interpreted</mat-hint>
                            </mat-form-field>

                            <mat-form-field>
                                <mat-label>Classify members as</mat-label>
                                <mat-select formControlName="classification">
                                    @for (opt of classificationOptions; track opt.value) {
                                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                                    }
                                </mat-select>
                                <mat-hint>Applied to every member in this file</mat-hint>
                            </mat-form-field>
                        </div>

                        <mat-form-field class="w-full">
                            <mat-label>Parking Type</mat-label>
                            <mat-select formControlName="parkingTypeId">
                                @for (type of allParkingTypes(); track getParkingTypeId(type)) {
                                <mat-option [value]="getParkingTypeId(type)">
                                    <div class="flex flex-col leading-tight py-0.5">
                                        <span class="font-medium">{{ getParkingTypeName(type) }}</span>
                                        <span class="text-xs text-gray-500">
                                            {{ getParkingTypeCategory(type) }}@if (getParkingTypeOperatorName(type)) {
                                            &middot; {{ getParkingTypeOperatorName(type) }} }@if
                                            (getParkingTypeOrgName(type)) { &middot; {{ getParkingTypeOrgName(type) }} }
                                        </span>
                                    </div>
                                </mat-option>
                                }
                                @if (allParkingTypes().length === 0) {
                                <mat-option disabled>No parking types available</mat-option>
                                }
                            </mat-select>
                            <mat-hint>Bulk member onboarding requires a Long-Term type</mat-hint>
                            <mat-error>Parking type is required</mat-error>
                        </mat-form-field>

                        <mat-form-field class="w-full">
                            <mat-label>Parking Zones (Optional)</mat-label>
                            <mat-select formControlName="zoneIds" multiple>
                                @for (zone of availableZones; track zone.id) {
                                <mat-option [value]="zone.id">
                                    {{ zone.name }}
                                </mat-option>
                                }
                            </mat-select>
                            <mat-hint>Select every zone this file's area covers. Leave empty to grant the whole space.</mat-hint>
                        </mat-form-field>

                        <mat-form-field class="w-full">
                            <mat-label>Max. cars inside at once (Optional)</mat-label>
                            <input matInput type="number" min="1" formControlName="maxConcurrentCars"
                                placeholder="Leave empty for no limit">
                            <mat-hint>
                                How many of a parker's cars may be inside this space at the same time.
                                Set 1 so a second car pays the short-term rate. Empty leaves every plate on the right admitted together.
                            </mat-hint>
                            <mat-error>Must be at least 1</mat-error>
                        </mat-form-field>

                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Organization <span class="text-red-500">*</span>
                            </label>
                            @if (selectedOrganization()) {
                            <div
                                class="border border-gray-300 rounded-lg p-3 flex items-center justify-between bg-gray-50">
                                <span class="text-sm font-medium">{{ selectedOrganization()!.name }}</span>
                                <button mat-icon-button (click)="clearSelectedOrganization()" type="button">
                                    <mat-icon>close</mat-icon>
                                </button>
                            </div>
                            } @else {
                            <mat-form-field class="w-full">
                                <mat-label>Search organization by name</mat-label>
                                <input matInput [ngModel]="organizationSearch()" [ngModelOptions]="{standalone: true}"
                                    (ngModelChange)="onOrganizationSearchChange($event)" placeholder="e.g. JKU" />
                                @if (isSearchingOrganizations()) {
                                <mat-icon matSuffix class="animate-spin">refresh</mat-icon>
                                }
                            </mat-form-field>
                            @if (organizationResults().length > 0) {
                            <div class="border border-gray-200 rounded-lg divide-y max-h-40 overflow-y-auto">
                                @for (org of organizationResults(); track org.id) {
                                <button type="button"
                                    class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                    (click)="selectOrganization(org)">
                                    {{ org.name }}
                                </button>
                                }
                            </div>
                            }
                            }
                            <p class="text-xs text-gray-500 mt-1">Members are added to this organization and the LongTerm right is billed to it</p>
                        </div>

                        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div class="flex items-start gap-2">
                                <mat-icon class="text-yellow-600 text-base mt-0.5">lightbulb</mat-icon>
                                <div class="text-sm">
                                    <p class="font-medium text-gray-800 mb-1">What this import does per row:</p>
                                    <ul class="list-disc list-inside text-gray-600 space-y-1">
                                        <li>Creates or matches a registered parker by email</li>
                                        <li>Registers and assigns the license plate(s)</li>
                                        <li>Adds the parker to the selected organization</li>
                                        <li>Sets the Employee/Student classification</li>
                                        <li>Creates or updates the Long-Term parking right</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </form>

                    @if (hasPreviewed()) {
                    <div class="mt-6">
                        <div class="flex items-center flex-wrap gap-2 mb-3">
                            <h3 class="text-base font-semibold text-gray-800">
                                {{ lastApplied() ? 'Import results' : 'Preview (dry run — nothing applied yet)' }}
                            </h3>
                            <button type="button" class="filter-chip" (click)="toggleResultStatusFilter('Created')"
                                [class.filter-chip--active]="resultStatusFilter() === 'Created'">
                                <jjames-badge variant="success">{{ resultCounts().Created }} created</jjames-badge>
                            </button>
                            <button type="button" class="filter-chip" (click)="toggleResultStatusFilter('Updated')"
                                [class.filter-chip--active]="resultStatusFilter() === 'Updated'">
                                <jjames-badge variant="info">{{ resultCounts().Updated }} updated</jjames-badge>
                            </button>
                            <button type="button" class="filter-chip" (click)="toggleResultStatusFilter('Unchanged')"
                                [class.filter-chip--active]="resultStatusFilter() === 'Unchanged'">
                                <jjames-badge variant="secondary">{{ resultCounts().Unchanged }} unchanged</jjames-badge>
                            </button>
                            <button type="button" class="filter-chip" (click)="toggleResultStatusFilter('Failed')"
                                [class.filter-chip--active]="resultStatusFilter() === 'Failed'">
                                <jjames-badge variant="danger">{{ resultCounts().Failed }} failed</jjames-badge>
                            </button>
                            @if (resultCounts().PartiallyApplied > 0) {
                            <button type="button" class="filter-chip" (click)="toggleResultStatusFilter('PartiallyApplied')"
                                [class.filter-chip--active]="resultStatusFilter() === 'PartiallyApplied'">
                                <jjames-badge variant="warning">{{ resultCounts().PartiallyApplied }} partial</jjames-badge>
                            </button>
                            }
                            @if (resultStatusFilter() !== 'all') {
                            <button type="button" class="text-xs text-gray-500 underline ml-1"
                                (click)="resultStatusFilter.set('all')">
                                Clear filter
                            </button>
                            }
                        </div>

                        <div class="border border-gray-200 rounded-lg overflow-x-auto max-h-80">
                            <table class="min-w-full text-sm">
                                <thead class="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th class="px-3 py-2 text-left font-medium text-gray-600">Row</th>
                                        <th class="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                                        <th class="px-3 py-2 text-left font-medium text-gray-600">Parker</th>
                                        <th class="px-3 py-2 text-left font-medium text-gray-600">Plates</th>
                                        <th class="px-3 py-2 text-left font-medium text-gray-600">Message</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-100">
                                    @for (row of filteredImportResults(); track row.rowNumber) {
                                    <tr>
                                        <td class="px-3 py-2 text-gray-500">{{ row.rowNumber }}</td>
                                        <td class="px-3 py-2">
                                            <jjames-badge [variant]="statusBadgeVariant(row.status)">{{ row.status }}</jjames-badge>
                                        </td>
                                        <td class="px-3 py-2">{{ row.parkerEmail || '—' }}</td>
                                        <td class="px-3 py-2">{{ (row.plates || []).join(', ') || row.licencePlate || '—' }}</td>
                                        <td class="px-3 py-2 text-gray-600">
                                            {{ row.message }}
                                            @if (row.failedStep) {
                                            <span class="text-xs text-gray-400">({{ row.failedStep }})</span>
                                            }
                                        </td>
                                    </tr>
                                    } @empty {
                                    <tr>
                                        <td colspan="5" class="px-3 py-4 text-center text-gray-400 italic">No rows match this filter.</td>
                                    </tr>
                                    }
                                </tbody>
                            </table>
                        </div>

                        @if (resultCounts().Failed + resultCounts().PartiallyApplied > 0) {
                        <button mat-button color="warn" class="mt-2" (click)="downloadErrorRows()" type="button">
                            <mat-icon>download</mat-icon> Download failed rows
                        </button>
                        }
                    </div>
                    }

                    <mat-dialog-actions class="flex justify-end gap-2 mt-4">
                        <jjames-button [variant]="'secondary'" (clicked)="onCancel()" [disabled]="isLoading()">
                            Close
                        </jjames-button>
                        <jjames-button [variant]="'outline'" (clicked)="onPreview()"
                            [disabled]="form.invalid || !selectedFile || isLoading()" matIconLeft="visibility">
                            @if (isLoading() && !lastApplied()) {
                            <mat-spinner diameter="20" class="inline-block mr-2"></mat-spinner>
                            Previewing...
                            } @else {
                            Preview (dry run)
                            }
                        </jjames-button>
                        <jjames-button [variant]="'primary'" (clicked)="onApply()"
                            [disabled]="form.invalid || !selectedFile || isLoading() || !hasPreviewed()" matIconLeft="upload">
                            @if (isLoading() && lastApplied()) {
                            <mat-spinner diameter="20" class="inline-block mr-2"></mat-spinner>
                            Applying...
                            } @else {
                            Apply import
                            }
                        </jjames-button>
                    </mat-dialog-actions>
                </div>
            </mat-tab>

            @if (canViewSyncLogs()) {
            <mat-tab label="Sync Logs">
                <div class="tab-content">
                    <app-migration-sync-logs></app-migration-sync-logs>
                </div>
            </mat-tab>
            }
        </mat-tab-group>
    </mat-dialog-content>
</div>
